class SfwRelatedProductsSlider extends HTMLElement {
  constructor() {
    super();
    this.realSlideCount = 0;
    this.isLooping = false;
    this.scrollRaf = null;
    this.onScroll = this.handleScroll.bind(this);
    this.onScrollEnd = this.handleScrollEnd.bind(this);
    this.onDotClick = this.handleDotClick.bind(this);
    this.onResize = () => this.init();
    this.onRecommendationsLoaded = (event) => {
      if (!event.target.contains(this)) return;
      requestAnimationFrame(() => requestAnimationFrame(() => this.init()));
    };
  }

  connectedCallback() {
    this.track = this.querySelector('.sfw-related-products__track');
    this.dotsWrapper = this.querySelector('.sfw-related-products__dots');
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.desktopQuery.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize);
    this.closest('product-recommendations')?.addEventListener(
      'sfw:recommendations:loaded',
      this.onRecommendationsLoaded
    );

    requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    this.teardown();
    this.desktopQuery?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
    this.closest('product-recommendations')?.removeEventListener(
      'sfw:recommendations:loaded',
      this.onRecommendationsLoaded
    );
  }

  teardown() {
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('scrollend', this.onScrollEnd);
    }

    this.dotsWrapper?.querySelectorAll('[data-slide-index]').forEach((dot) => {
      dot.removeEventListener('click', this.onDotClick);
    });

    this.removeClones();
    cancelAnimationFrame(this.scrollRaf);
    clearTimeout(this.scrollEndTimer);
  }

  init() {
    this.teardown();

    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('.sfw-related-products__slide:not(.is-clone)'));
    this.realSlideCount = this.slides.length;
    this.dots = Array.from(this.dotsWrapper?.querySelectorAll('[data-slide-index]') || []);

    if (this.desktopQuery.matches || this.realSlideCount < 2) {
      this.classList.remove('is-active');
      this.removeClones();
      if (this.dotsWrapper) this.dotsWrapper.hidden = true;
      return;
    }

    this.classList.add('is-active');
    if (this.dotsWrapper) this.dotsWrapper.hidden = false;
    this.setupInfinite();

    requestAnimationFrame(() => {
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', this.onScrollEnd, { passive: true });
      }
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.updateDots();
    });
  }

  setupInfinite() {
    this.removeClones();
    this.slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      this.track.appendChild(clone);
    });
    this.track.scrollLeft = 0;
  }

  removeClones() {
    this.track?.querySelectorAll('.sfw-related-products__slide.is-clone').forEach((clone) => clone.remove());
  }

  getSetWidth() {
    if (!this.slides.length) return 0;
    const first = this.slides[0];
    const last = this.slides[this.slides.length - 1];
    return last.offsetLeft + last.offsetWidth - first.offsetLeft;
  }

  getGap() {
    const styles = window.getComputedStyle(this.track);
    return parseFloat(styles.columnGap || styles.gap || '0') || 0;
  }

  getSlideStride() {
    if (this.slides.length < 2) {
      return this.slides[0]?.offsetWidth || 0;
    }
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  }

  getActiveIndex() {
    const stride = this.getSlideStride();
    if (!stride) return 0;
    const index = Math.round(this.track.scrollLeft / stride);
    return ((index % this.realSlideCount) + this.realSlideCount) % this.realSlideCount;
  }

  handleScroll() {
    cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(() => this.updateDots());

    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => this.handleScrollEnd(), 120);
  }

  handleScrollEnd() {
    if (this.isLooping || this.desktopQuery.matches) return;

    const setWidth = this.getSetWidth();
    if (!setWidth) return;

    if (this.track.scrollLeft >= setWidth - 2) {
      this.isLooping = true;
      this.track.scrollLeft -= setWidth;
      this.isLooping = false;
    }

    this.updateDots();
  }

  handleDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.slideIndex, 10);
    if (Number.isNaN(index)) return;
    this.scrollToIndex(index, true);
  }

  scrollToIndex(index, smooth) {
    const slide = this.slides[index];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
    this.updateDots(index);
  }

  updateDots(forcedIndex) {
    if (!this.dots.length) return;

    const activeIndex = typeof forcedIndex === 'number' ? forcedIndex : this.getActiveIndex();

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('slider-counter__link--active', index === activeIndex);
    });
  }
}

customElements.define('sfw-related-products-slider', SfwRelatedProductsSlider);
